import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Video, Clock, CalendarDays, Play, Lock, Loader2,
  ExternalLink, Sparkles, FileText, BadgeCheck, Paperclip, Presentation, ListChecks, FileQuestion, Download,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const ATTACHMENT_ICON = {
  ppt: Presentation, worksheet: ListChecks, quiz: FileQuestion, other: FileText,
};
const ATTACHMENT_LABEL = {
  ppt: "PPT", worksheet: "Worksheet", quiz: "Quiz", other: "File",
};

async function downloadAttachment(att) {
  try {
    // Auth-gated proxy: use axios (adds Bearer token), fetch as blob, save.
    const r = await api.get(att.url.replace(/^\/api/, ""), { responseType: "blob" });
    const blob = new Blob([r.data], { type: r.headers?.["content-type"] || "application/octet-stream" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = att.filename || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
  } catch (err) {
    const msg = err?.response?.status === 402 ? "Your subscription is inactive."
                : err?.response?.status === 410 ? "This file has expired."
                : "Could not download the file.";
    toast.error(msg);
  }
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    weekday: "short", day: "numeric", month: "short",
    hour: "numeric", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

function relativeTime(iso) {
  if (!iso) return "";
  const now = new Date();
  const t = new Date(iso);
  const diffMin = Math.round((t - now) / 60000);
  if (Math.abs(diffMin) < 60) return diffMin > 0 ? `in ${diffMin} min` : `${-diffMin} min ago`;
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) return diffH > 0 ? `in ${diffH} h` : `${-diffH} h ago`;
  const diffD = Math.round(diffH / 24);
  return diffD > 0 ? `in ${diffD} d` : `${-diffD} d ago`;
}

export default function JidnyasaDashboard() {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [entitlements, setEntitlements] = useState({});
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/jidnyasa/dashboard"),
        api.get("/billing/status").catch(() => ({ data: { entitlements: {} } })),
      ]);
      setState(r.data);
      setEntitlements(s.data.entitlements || {});
    } catch (e) {
      toast.error("Could not load Jidnyasa dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const joinSession = async (sessionId) => {
    setJoiningId(sessionId);
    try {
      const r = await api.post(`/jidnyasa/sessions/${sessionId}/join`);
      const url = r.data?.meeting_url;
      if (!url) throw new Error("No meeting URL returned");
      // Open in a new tab — meeting link never persisted in the DOM.
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening live session…");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not join session";
      toast.error(detail);
    } finally { setJoiningId(null); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!state) return null;

  const hasSub = state.has_active_subscription;
  const nextSession = state.upcoming?.[0];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="jidnyasa-dashboard">
      <header className="max-w-4xl mx-auto px-6 sm:px-8 pt-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/home")} className="p-2 rounded-full hover:bg-white/70"
                  data-testid="jidnyasa-back-btn">
            <ArrowLeft className="w-5 h-5"/>
          </button>
          <Logo size={80} />
        </div>
        <Link to="/jidnyasa" className="text-sm text-muted-foreground hover:underline">
          Program overview
        </Link>
      </header>

      <div className="max-w-4xl mx-auto px-6 sm:px-8 pt-8">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
          JJLP · Jidnyasa Jr Leaders · जिज्ञासा
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-2">Your live sessions</h1>

        {/* Subscription status card */}
        <div className={`mt-8 rounded-3xl p-5 sm:p-6 ${hasSub
          ? "bg-[hsl(var(--neu-green))]/10 border border-[hsl(var(--neu-green))]/25"
          : "bg-[hsl(var(--neu-pink))]/10 border border-[hsl(var(--neu-pink))]/25"}`}
             data-testid="jidnyasa-sub-status">
          <div className="flex items-start gap-3">
            {hasSub ? (
              <BadgeCheck className="w-6 h-6 text-[hsl(var(--neu-green))] shrink-0 mt-0.5"/>
            ) : (
              <Lock className="w-6 h-6 text-[hsl(var(--neu-pink))] shrink-0 mt-0.5"/>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl">
                {hasSub ? "Subscription active" : "Subscription inactive"}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {hasSub
                  ? state.subscription?.valid_until
                      ? `Access continues until ${new Date(state.subscription.valid_until).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.`
                      : "You have full access to live sessions and recordings."
                  : "Your Jidnyasa subscription is inactive. Please subscribe to join live sessions and view recordings."}
              </p>
              {!hasSub && (
                <Link to="/paywall?src=jidnyasa" className="btn-primary text-sm mt-4"
                      data-testid="jidnyasa-view-plans-btn">
                  View plans
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Cross-sell: NeuroEQ Sunday ritual (shown when user has Jidnyasa but not NeuroEQ) */}
        {entitlements.jidnyasa && !entitlements.neuroeq && (
          <Link to="/paywall?src=neuroeq" data-testid="jidnyasa-crosssell-neuroeq"
                className="mt-8 block rounded-3xl p-5 bg-[hsl(var(--neu-orange))]/10 border border-[hsl(var(--neu-orange))]/30">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
              Your family might also enjoy
            </div>
            <div className="flex items-start justify-between gap-4 mt-1">
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl">NeuroEQ · Sunday family ritual</div>
                <p className="text-sm text-muted-foreground mt-1">
                  30 minutes together each Sunday — one story, one talk, one activity, one reflection. Ages 4–13.
                </p>
                <span className="text-xs text-[hsl(var(--neu-orange))] font-medium mt-2 inline-flex items-center gap-1">
                  Start free (2 Sundays) <ArrowRight className="w-3 h-3"/>
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Upcoming session */}
        <div className="mt-10">
          <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[hsl(var(--neu-orange))]"/> Upcoming
          </h2>
          {!nextSession ? (
            <div className="card-soft p-6 text-muted-foreground text-sm">
              No upcoming session scheduled yet. Check back soon — we drop the next topic each week.
            </div>
          ) : (
            <div className="card-elevated p-6" data-testid="jidnyasa-upcoming-card">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
                {relativeTime(nextSession.scheduled_at)} · {nextSession.status}
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mt-1">{nextSession.topic}</h3>
              {nextSession.description && (
                <p className="text-muted-foreground mt-2">{nextSession.description}</p>
              )}
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4 text-[hsl(var(--neu-orange))]"/>
                  {formatDate(nextSession.scheduled_at)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[hsl(var(--neu-orange))]"/>
                  {nextSession.duration_minutes} min
                </span>
              </div>
              {hasSub ? (
                <button onClick={() => joinSession(nextSession.id)}
                         disabled={joiningId === nextSession.id}
                         className="btn-primary mt-6 text-lg"
                         data-testid="jidnyasa-join-btn">
                  {joiningId === nextSession.id
                    ? <Loader2 className="w-4 h-4 animate-spin"/>
                    : <><Video className="w-5 h-5"/> Join JJLP Live</>}
                </button>
              ) : (
                <Link to="/paywall?src=jidnyasa" className="btn-primary mt-6 text-lg">
                  <Lock className="w-4 h-4"/> Subscribe to join
                </Link>
              )}

              {/* Pre-class kit (if the admin uploaded slides / worksheet ahead of time) */}
              {hasSub && nextSession.attachments && nextSession.attachments.length > 0 && (
                <div className="mt-6 pt-4 border-t border-black/5">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Paperclip className="w-3 h-3"/> Pre-class kit
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {nextSession.attachments.map((a) => {
                      const Icon = ATTACHMENT_ICON[a.kind] || FileText;
                      return (
                        <button key={a.id}
                            onClick={() => downloadAttachment(a)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--neu-paper))] border border-black/5 text-xs hover:border-[hsl(var(--neu-orange))]/30 transition-colors"
                            data-testid={`jidnyasa-upcoming-attachment-${a.id}`}>
                          <Icon className="w-3.5 h-3.5 text-[hsl(var(--neu-orange))]"/>
                          <span className="font-medium">{ATTACHMENT_LABEL[a.kind] || "File"}</span>
                          <span className="text-muted-foreground truncate max-w-[180px]">{a.filename}</span>
                          <Download className="w-3 h-3 text-muted-foreground"/>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Past sessions */}
        {state.past && state.past.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display text-2xl mb-4">Past sessions</h2>
            <div className="space-y-3">
              {state.past.map((s) => (
                <div key={s.id} className="card-soft p-5 flex flex-col gap-3"
                      data-testid={`jidnyasa-past-${s.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg">{s.topic}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(s.scheduled_at)}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {s.has_recording ? (
                      hasSub && s.recording_url ? (
                        <a href={s.recording_url} target="_blank" rel="noopener noreferrer"
                            className="btn-ghost text-sm px-4 py-2">
                          <Play className="w-4 h-4"/> Watch recording <ExternalLink className="w-3 h-3"/>
                        </a>
                      ) : (
                        <span className="btn-ghost text-sm px-4 py-2 opacity-60 cursor-not-allowed">
                          <Lock className="w-4 h-4"/> Recording locked
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">Recording will appear soon</span>
                    )}
                    {hasSub && s.materials_url && (
                      <a href={s.materials_url} target="_blank" rel="noopener noreferrer"
                          className="btn-ghost text-sm px-4 py-2">
                        <FileText className="w-4 h-4"/> Materials
                      </a>
                    )}
                  </div>
                  </div>

                  {/* Session kit downloads (auto-expire after 14 days) */}
                  {hasSub && s.attachments && s.attachments.length > 0 && (
                    <div className="pt-3 border-t border-black/5">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                        <Paperclip className="w-3 h-3"/> Session kit
                        <span className="normal-case tracking-normal text-muted-foreground/70">· available for 14 days after upload</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {s.attachments.map((a) => {
                          const Icon = ATTACHMENT_ICON[a.kind] || FileText;
                          return (
                            <button key={a.id}
                                onClick={() => downloadAttachment(a)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--neu-paper))] border border-black/5 text-xs hover:border-[hsl(var(--neu-orange))]/30 transition-colors"
                                data-testid={`jidnyasa-attachment-${a.id}`}>
                              <Icon className="w-3.5 h-3.5 text-[hsl(var(--neu-orange))]"/>
                              <span className="font-medium">{ATTACHMENT_LABEL[a.kind] || "File"}</span>
                              <span className="text-muted-foreground truncate max-w-[180px]">{a.filename}</span>
                              <Download className="w-3 h-3 text-muted-foreground"/>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
